import React, { useEffect, useMemo, useRef, useState } from "react";

import { addMonths, classNames, diffInMonths, transformDateToLocalDateString } from "helpers";

import { RENT_DURATION_PRESETS, RentDurationPreset } from "./consts";
import { DateRangeRentPickerProps, RentPeriodValues } from "./types";

const WEEK_DAYS = ["PON", "UTO", "SRI", "ČET", "PET", "SUB", "NED"];
const GRID_CELLS = 42; // 6 tjedana
const MONTHS = [
  "Siječanj", "Veljača", "Ožujak", "Travanj", "Svibanj", "Lipanj",
  "Srpanj", "Kolovoz", "Rujan", "Listopad", "Studeni", "Prosinac",
];

/**
 * S API-ja datumi stižu kao ISO stringovi iako ih tip najavljuje kao Date (transformacije ih
 * propuštaju kakvi jesu), pa se prije svakog računanja moraju pretvoriti — inače puca .getTime().
 */
const toDate = (value: Date | string | null | undefined): Date | null => {
  if (!value) return null;

  const date = value instanceof Date ? value : new Date(value);

  return isNaN(date.getTime()) ? null : date;
};

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const sameDay = (a: Date | null, b: Date | null) =>
  !!a && !!b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const addDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);

  return result;
};

/**
 * Trajanje u mjesecima za razdoblje koje uključuje i zadnji dan: najam 01.08.2026. – 31.07.2027.
 * traje 12 mjeseci, pa se mjeseci broje do dana nakon završetka.
 */
export const rentMonthsBetween = (start: Date, end: Date): number =>
  diffInMonths(start, addDays(end, 1));

/** Razdoblje iz brzog odabira: od zadanog početka (ili danas) za n mjeseci/dana. */
const resolvePreset = (preset: RentDurationPreset, start: Date | null): Partial<RentPeriodValues> => {
  if (preset.openEnded) {
    return { rentStartDate: start ?? startOfDay(new Date()), rentEndDate: null, rentDurationMonths: null };
  }

  const from = start ?? startOfDay(new Date());

  const to = preset.months
    ? addDays(addMonths(from, preset.months), -1)
    : addDays(from, (preset.days ?? 0) - 1);

  return {
    rentStartDate: from,
    rentEndDate: to,
    rentDurationMonths: preset.months ?? null,
  };
};

/**
 * Razdoblje najma u jednoj kontroli — izgled i ponašanje kao DateRangePicker na dashboardu:
 * klik od-do u kalendaru ili gotovo trajanje iz brzog odabira, uključujući „Neodređeno".
 */
const DateRangeRentPicker: React.FC<DateRangeRentPickerProps> = (props) => {
  const {
    rentStartDate,
    rentEndDate,
    rentDurationMonths,
    onChange,
    label = "Razdoblje najma",
    error,
    disabled,
    align = "left",
  } = props;
  // Sve dalje radi s pravim Date objektima, neovisno o tome što je stiglo kroz props.
  const startDate = toDate(rentStartDate);
  const endDate = toDate(rentEndDate);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const [draftFrom, setDraftFrom] = useState<Date | null>(startDate);
  const [draftTo, setDraftTo] = useState<Date | null>(endDate);

  const initialView = startDate ?? endDate ?? new Date();
  const [viewYear, setViewYear] = useState(initialView.getFullYear());
  const [viewMonth, setViewMonth] = useState(initialView.getMonth());

  useEffect(() => {
    if (!open) return;

    setDraftFrom(startDate);
    setDraftTo(endDate);

    const base = startDate ?? endDate ?? new Date();
    setViewYear(base.getFullYear());
    setViewMonth(base.getMonth());
  }, [open, startDate, endDate]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Mreža počinje ponedjeljkom prije 1. u mjesecu i puni se do punih 6 tjedana — fiksna
  // visina, pa popover ne poskakuje pri promjeni mjeseca.
  const days = useMemo(() => {
    const firstDay = new Date(viewYear, viewMonth, 1);
    const offset = (firstDay.getDay() + 6) % 7;

    return Array.from({ length: GRID_CELLS }, (_, i) => {
      const date = new Date(viewYear, viewMonth, i - offset + 1);
      return { date, isOutside: date.getMonth() !== viewMonth };
    });
  }, [viewYear, viewMonth]);

  const isOpenEnded = !!startDate && !endDate;

  const triggerLabel = useMemo(() => {
    if (!startDate && !endDate) return "Odaberite razdoblje";

    if (isOpenEnded) return `${transformDateToLocalDateString(startDate)} — neodređeno`;

    const period = `${transformDateToLocalDateString(startDate)} – ${transformDateToLocalDateString(endDate)}`;

    return rentDurationMonths ? `${period} (${rentDurationMonths} mj.)` : period;
  }, [startDate, endDate, rentDurationMonths, isOpenEnded]);

  const activePresetId = useMemo(() => {
    if (isOpenEnded) return "open";
    if (!startDate || !endDate) return null;

    const months = rentMonthsBetween(startDate, endDate);
    const totalDays = Math.round((endDate.getTime() - startDate.getTime()) / 86_400_000) + 1;

    return RENT_DURATION_PRESETS.find(preset =>
      preset.months ? preset.months === months : preset.days === totalDays
    )?.id ?? null;
  }, [startDate, endDate, isOpenEnded]);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };

  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const applyPreset = (preset: RentDurationPreset) => {
    onChange(resolvePreset(preset, startDate));
    setOpen(false);
  };

  const handleDayClick = (day: Date) => {
    // Nema početka ili je razdoblje već kompletno → počinje novi odabir.
    if (!draftFrom || (draftFrom && draftTo)) {
      setDraftFrom(day);
      setDraftTo(null);
      onChange({ rentStartDate: startOfDay(day), rentEndDate: null, rentDurationMonths: null });
      return;
    }

    const from = draftFrom <= day ? draftFrom : day;
    const to = draftFrom <= day ? day : draftFrom;

    setDraftFrom(from);
    setDraftTo(to);

    const months = rentMonthsBetween(from, to);

    onChange({
      rentStartDate: startOfDay(from),
      rentEndDate: startOfDay(to),
      rentDurationMonths: months > 0 ? months : null,
    });
  };

  const handleClear = () => {
    setDraftFrom(null);
    setDraftTo(null);
    onChange({ rentStartDate: null, rentEndDate: null, rentDurationMonths: null });
  };

  const inRange = (day: Date) => draftFrom && draftTo && day >= startOfDay(draftFrom) && day <= startOfDay(draftTo);
  const isEdge = (day: Date) => sameDay(day, draftFrom) || sameDay(day, draftTo);

  const hint = !draftFrom
    ? "Klikni početak najma"
    : !draftTo
      ? "Klikni završetak ili odaberi trajanje"
      : "Razdoblje je postavljeno";

  return (
    <div ref={wrapperRef} className="relative">
      {label && (
        <label className="block text-sm mb-2 font-medium leading-6 text-[#344054]">{label}</label>
      )}

      {/* Iste klase kao Dropdown/DatePicker — kontrola mora izgledati kao ostala polja obrasca. */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
        className={classNames(
          // py-2.5 — ista visina kao TextField/NumberField u istom redu.
          "relative w-full flex items-center gap-2 rounded-sm pl-3 pr-3 py-2.5 text-left sm:text-sm sm:leading-6 ring-1 focus:outline-none",
          disabled
            ? "ring-slate-300 bg-gray-100 text-gray-900 cursor-default"
            : error
              ? "ring-red-500 bg-red-50 text-red-600 cursor-pointer"
              : "ring-slate-300 bg-slate-50 text-gray-800 cursor-pointer focus:ring-slate-400"
        )}
      >
        <img src="/media/icons/calendar.svg" alt="" className="w-4 h-4 opacity-70" />
        <span className={classNames("whitespace-nowrap", !startDate && !error && "text-gray-400")}>
          {triggerLabel}
        </span>
        <svg
          className={classNames("w-4 h-4 ml-auto text-slate-400 transition-transform", open && "rotate-180")}
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
        </svg>
      </button>

      {error && <div className="text-sm text-red-600 mt-1">{error}</div>}

      {open && (
        <div
          className={classNames(
            "absolute z-50 mt-2 w-80 rounded-sm bg-white p-4 ring-1 ring-slate-200 shadow-lg",
            align === "right" ? "right-0" : "left-0"
          )}
        >
          <div className="flex items-center justify-between mb-3">
            <button type="button" onClick={prevMonth} className="w-7 h-7 flex items-center justify-center rounded-sm text-slate-500 hover:bg-slate-100">
              ‹
            </button>
            <span className="text-sm font-semibold text-slate-800">
              {MONTHS[viewMonth]} {viewYear}
            </span>
            <button type="button" onClick={nextMonth} className="w-7 h-7 flex items-center justify-center rounded-sm text-slate-500 hover:bg-slate-100">
              ›
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-1 text-center">
            {WEEK_DAYS.map(day => (
              <div key={day} className="text-[11px] font-semibold text-slate-400">{day}</div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1 text-center">
            {days.map(({ date, isOutside }, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleDayClick(date)}
                className={classNames(
                  "h-8 text-sm rounded-sm transition-colors",
                  isEdge(date)
                    ? "bg-sky-600 text-white font-semibold"
                    : inRange(date)
                      ? "bg-sky-100 text-sky-800"
                      : isOutside
                        ? "text-slate-300 hover:bg-sky-50"
                        : "text-slate-700 hover:bg-sky-50"
                )}
              >
                {date.getDate()}
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100 text-xs">
            <span className="text-slate-500">{hint}</span>
            <button type="button" onClick={handleClear} className="text-slate-500 hover:text-slate-800 font-medium">
              Očisti
            </button>
          </div>

          <div className="mt-3">
            <div className="text-[11px] font-semibold text-slate-400 mb-2">TRAJANJE NAJMA</div>
            <div className="flex flex-wrap gap-1.5">
              {RENT_DURATION_PRESETS.map(preset => (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => applyPreset(preset)}
                  className={classNames(
                    "px-2.5 py-1 text-xs rounded-sm transition-colors",
                    activePresetId === preset.id
                      ? "ring-1 ring-sky-500 text-sky-700 bg-sky-50"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  )}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangeRentPicker;
